import { useEffect, useId, useReducer, useState, type FocusEvent, type MouseEvent, type PointerEvent } from "react";

function isHoverTarget(target: EventTarget | null, point: { clientX: number; clientY: number }, owner: string): boolean {
  if (target === null) return false;
  if (target instanceof Element && target.closest("[data-tooltip-owner]")?.getAttribute("data-tooltip-owner") === owner) return true;
  const tooltip = [...document.querySelectorAll(".app-anchored-tooltip")]
    .find((element) => element.getAttribute("data-tooltip-owner") === owner);
  const surface = tooltip?.firstElementChild;
  if (tooltip === undefined || surface === null || surface === undefined) return false;
  const bounds = tooltip.getBoundingClientRect();
  const top = surface.getBoundingClientRect().top;
  // This corridor keeps hover alive without putting a hit target over controls below it.
  return point.clientX >= bounds.left && point.clientX < bounds.right
    && point.clientY >= bounds.top && point.clientY < top;
}

export interface ShownTooltip<T> {
  readonly content: T;
  /** The hovered or focused element; the tooltip is placed under it on every render. */
  readonly anchor: Element;
}

/**
 * When an `AnchoredTooltip` shows, and what it says: while its element is hovered or
 * focused, or while the pointer is over its tooltip. Hover wins over focus, and leaving
 * one keeps the other. It follows its element when a pane scrolls or the window resizes,
 * and Escape dismisses it (WCAG 1.4.13). An
 * element a render removes, such as an action that went away, takes its tooltip with it.
 * `content` says which element's text to show, for a tooltip shared by several; one
 * element alone needs none.
 */
export function useAnchoredTooltip<T = void>() {
  const owner = useId();
  const [hovered, setHovered] = useState<ShownTooltip<T> | null>(null);
  const [focused, setFocused] = useState<ShownTooltip<T> | null>(null);
  const [, follow] = useReducer((tick: number) => tick + 1, 0);
  const candidate = hovered ?? focused;
  const shown = candidate?.anchor.isConnected ? candidate : null;
  const open = shown !== null;
  const anchor = shown?.anchor ?? null;

  // A render that removes the element ran the check above while it was still there, and no
  // event says it left; render again once it has, so its tooltip goes with it.
  useEffect(() => {
    if (anchor === null) {
      return;
    }
    const observer = new MutationObserver(() => {
      if (!anchor.isConnected) follow();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, [anchor]);

  useEffect(() => {
    if (!open || anchor === null) {
      return;
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // This Escape dismissed the tooltip; nothing else acts on it, such as a page's Back (F87).
        event.preventDefault();
        setHovered(null);
        setFocused(null);
      }
    };
    // Pointer events retain fractional CSS coordinates; compatibility mouse events don't.
    const leaveHoverArea = (event: globalThis.PointerEvent) => {
      const target = event.type === "pointerout" ? event.relatedTarget : event.target;
      if (!isHoverTarget(target, event, owner)) {
        setHovered(null);
      }
    };
    // The tooltip is portalled; its pointer events don't pass through the anchor.
    document.addEventListener("pointerout", leaveHoverArea);
    document.addEventListener("pointermove", leaveHoverArea);
    // Capture, so scrolling any pane moves it along, not only the window.
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    // Capture too, so the tooltip has Escape before window-wide keys do.
    window.addEventListener("keydown", closeOnEscape, true);
    return () => {
      document.removeEventListener("pointerout", leaveHoverArea);
      document.removeEventListener("pointermove", leaveHoverArea);
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
      window.removeEventListener("keydown", closeOnEscape, true);
    };
  }, [open, anchor, owner]);

  return {
    shown,
    hoverProps: (content: T) => ({
      "data-tooltip-owner": owner,
      onMouseEnter: (event: MouseEvent) => { setHovered({ content, anchor: event.currentTarget }); },
      onPointerLeave: (event: PointerEvent) => {
        if (!isHoverTarget(event.relatedTarget, event, owner)) setHovered(null);
      },
    }),
    focusProps: (content: T) => ({
      "data-tooltip-owner": owner,
      onFocus: (event: Pick<FocusEvent, "currentTarget">) => { setFocused({ content, anchor: event.currentTarget }); },
      onBlur: () => { setFocused(null); },
    }),
  };
}
